// Intelligent route prefetcher.
// Triggers the dynamic import for a route as soon as the user shows
// intent to navigate (hover / focus / touchstart) — so when they finally
// click, the chunk is already in the browser cache and the page renders
// instantly.
//
// Works via global event delegation on the document, so EVERY <Link>
// or <a href="/..."> in the app benefits automatically — no need to
// refactor individual components.

// Map of route path → dynamic import factory.
// Must mirror App.js lazy() declarations.
// Webpack/CRA dedupes import() calls automatically: calling import()
// twice returns the same chunk.
const EXACT_IMPORTS = {
  "/demenagement-particulier": () => import("@/pages/Particulier"),
  "/entreprise": () => import("@/pages/Entreprise"),
  "/garde-meuble": () => import("@/pages/GardeMeuble"),
  "/monte-meuble": () => import("@/pages/MonteMeuble"),
  "/formules": () => import("@/pages/Formules"),
  "/calculer-mon-volume": () => import("@/pages/CalculateurVolume"),
  "/international": () => import("@/pages/International"),
  "/mutation-professionnelle": () => import("@/pages/MutationProfessionnelle"),
  "/debarras-vide-maison": () => import("@/pages/DebarrasVideMaison"),
  "/montage-demontage-meubles": () => import("@/pages/MontageDemontage"),
  "/demenagement-paris-75": () => import("@/pages/DemenagementParis75"),
  "/demenagement-seine-et-marne-77": () => import("@/pages/DemenagementSeineEtMarne77"),
  "/demenagement-yvelines-78": () => import("@/pages/DemenagementYvelines78"),
  "/demenagement-essonne-91": () => import("@/pages/DemenagementEssonne91"),
  "/demenagement-hauts-de-seine-92": () => import("@/pages/DemenagementHautsDeSeine92"),
  "/demenagement-seine-saint-denis-93": () => import("@/pages/DemenagementSeineSaintDenis93"),
  "/demenagement-val-de-marne-94": () => import("@/pages/DemenagementValDeMarne94"),
  "/demenagement-val-d-oise-95": () => import("@/pages/DemenagementValdOise95"),
  "/demenagement-militaire": () => import("@/pages/DemenagementMilitaire"),
  "/mentions-legales": () => import("@/pages/MentionsLegales"),
  "/politique-de-confidentialite": () => import("@/pages/PolitiqueConfidentialite"),
  "/faq": () => import("@/pages/FAQ"),
  "/presentation": () => import("@/pages/Presentation"),
  "/contact": () => import("@/pages/Contact"),
  "/conditions-generales-dutilisation": () => import("@/pages/ConditionsGenerales"),
  "/utilisation-des-cookies": () => import("@/pages/UtilisationCookies"),
  "/autorisation-de-stationnement-demenagement": () => import("@/pages/AutorisationStationnement"),
  "/autorisation-de-stationnement-demenagement-paris": () => import("@/pages/AutorisationStationnementParis"),
  "/autorisation-de-stationnement-demenagement-antony": () => import("@/pages/AutorisationStationnementAntony"),
  "/aot-yvelines-78": () => import("@/pages/AotYvelines78"),
  "/aot-hauts-de-seine-92": () => import("@/pages/AotHautsDeSeine92"),
  "/aot-val-de-marne-94": () => import("@/pages/AotValDeMarne94"),
  "/demande-de-devis": () => import("@/pages/Devis"),
};

// Prefix routes — same chunk regardless of city slug.
// All city AOT pages share the same big chunk (AutorisationVilleTemplate
// + villes-aot-data.js).
const PREFIX_IMPORTS = [
  {
    test: "/autorisation-de-stationnement-demenagement-",
    importer: () => import("@/pages/AutorisationVilleTemplate"),
  },
];

// Already-triggered paths, to avoid re-firing imports on every hover.
const prefetched = new Set();

function findImporter(pathname) {
  if (EXACT_IMPORTS[pathname]) {
    return EXACT_IMPORTS[pathname];
  }
  for (const { test, importer } of PREFIX_IMPORTS) {
    if (pathname.startsWith(test)) {
      return importer;
    }
  }
  return null;
}

function prefetchRoute(pathname) {
  if (prefetched.has(pathname)) return;
  const importer = findImporter(pathname);
  if (!importer) return;
  prefetched.add(pathname);
  // Defer to idle to avoid competing with main-thread work.
  const run = () => {
    importer().catch(() => prefetched.delete(pathname));
  };
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 1500 });
  } else {
    setTimeout(run, 50);
  }
}

function extractPathname(href) {
  if (!href) return null;
  if (href.startsWith("/")) return href.split("#")[0].split("?")[0];
  // Same-origin absolute URLs
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      return url.pathname;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function handleIntent(event) {
  const anchor = event.target.closest && event.target.closest("a");
  if (!anchor) return;
  const href = anchor.getAttribute("href");
  const pathname = extractPathname(href);
  if (pathname) {
    prefetchRoute(pathname);
  }
}

let installed = false;

export function installRoutePrefetch() {
  if (installed || typeof document === "undefined") return;
  installed = true;
  // mouseover → desktop hover intent
  document.addEventListener("mouseover", handleIntent, { passive: true, capture: true });
  // focusin → keyboard navigation intent
  document.addEventListener("focusin", handleIntent, { passive: true, capture: true });
  // touchstart → mobile, fires ~80 ms before click
  document.addEventListener("touchstart", handleIntent, { passive: true, capture: true });
}
